import { find } from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getWindow, trackNavigatedToPage } from '@surfline/web-common';
import { useMount } from 'react-use';
import { Box } from '@mui/material';
import TravelHero from 'components/TravelHero';
import TravelZonePlaylist from 'components/TravelZonePlaylist';
import TravelAllZones from 'components/TravelAllZones';
import TravelRecentArticles from 'components/TravelRecentArticles';
import TravelSeriesPlaylist from 'components/TravelSeriesPlaylist';
import config from 'config';
import type { AppState } from 'stores';
import type {
  HeroSectionType,
  TravelZonePlaylistType,
  TravelSeriesPlaylistType,
  LandingPageSectionsType,
  TravelAllZonesType,
} from 'types/contentful/content-types';
import type { LandingPage } from 'types/contentful/queries';

import Styles from './Travel.module.scss';

export const LandingPageSections = ({
  sections,
  ssrReduxState,
}: {
  sections: LandingPageSectionsType;
  ssrReduxState: AppState;
}) =>
  sections?.map(
    (
      section:
        | HeroSectionType
        | TravelZonePlaylistType
        | TravelSeriesPlaylistType
        | TravelAllZonesType,
      index: number,
    ) => {
      const sectionType = section?.__typename;

      switch (sectionType) {
        case 'TravelZonePlaylist':
          return (
            <TravelZonePlaylist
              key={`${index + 1}-zone-playlist-section`}
              data={section}
              pageTitle="travel homepage"
            />
          );
        case 'TravelSeriesPlaylist':
          return (
            <TravelSeriesPlaylist
              key={`${index + 1}-series-playlist-section`}
              data={section}
              ssrReduxState={ssrReduxState}
            />
          );
        case 'HeroSection':
          return <TravelHero key={`${index + 1}-hero-section`} data={section} />;
        case 'TravelAllZones':
          return (
            <TravelAllZones
              key={`${index + 1}-all-zones-section`}
              data={section as TravelAllZonesType}
            />
          );
        default:
          return null;
      }
    },
  );

type Props = {
  ssrReduxState: AppState;
  page: LandingPage;
};

const Travel: React.FC<Props> = (props) => {
  const {
    page: {
      travelLandingPage: {
        travelLandingPageSectionCollection: { items },
      },
    },
  } = props;

  const sections = Array.isArray(items) ? items : [];

  const heroSection = find(items, {
    __typename: 'HeroSection',
  }) as HeroSectionType;

  const { asPath } = useRouter();

  useMount(() => {
    trackNavigatedToPage('Page Viewed', {
      category: 'travel',
      path: asPath,
      referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
      search: getWindow().location.search,
      title: 'travel homepage',
      url: `${getWindow()?.location?.href ?? ''}${asPath}`,
    });
  });

  if (!sections?.length) return null;

  return (
    <>
      <Head>
        <title>{config.meta.title.travel.landing}</title>
        <meta
          property="og:image"
          content={config.cloudflareImageResizingUrl(
            heroSection?.heroPoster?.url ?? config.defaultImageUrl,
            'h=630,w=1200,q=85,f=auto,fit=cover',
          )}
        />
      </Head>
      <Box className={Styles.landingWrap}>
        {LandingPageSections({ sections, ssrReduxState: props.ssrReduxState })}
        <TravelRecentArticles {...props} />
      </Box>
    </>
  );
};

export default Travel;
